/*
 * <media-gallery>: Obraven product gallery (snippets/product-media-gallery.liquid).
 * The main track is a scroll-snap slider (native swipe on touch); arrows, thumbnails, tap zones
 * (left/right 40% of the image on touch) and arrow keys move between slides. Keeps the counter,
 * dots, thumbnail state and the zoom button's target media in sync.
 */
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.index = 0;
        this.onScroll = this.onScroll.bind(this);
      }

      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        this.dots = Array.from(this.querySelectorAll('.ob-gallery__dot'));
        this.current = this.querySelector('[data-gallery-current]');
        this.status = this.querySelector('[data-gallery-status]');
        this.zoom = this.querySelector('[data-gallery-zoom]');
        if (!this.track || this.slides.length < 2) return;

        this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
        this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => this.goTo(parseInt(thumb.dataset.galleryThumb, 10)))
        );
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
          if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        });

        // Tap zones on touch screens: left 40% goes back, right 40% goes forward.
        this.track.addEventListener('pointerdown', (event) => {
          this.pointerStart = event.pointerType === 'mouse' ? null : { x: event.clientX, y: event.clientY };
        });
        this.track.addEventListener('pointerup', (event) => {
          if (!this.pointerStart || event.target.closest('button, a, deferred-media, product-model')) return;
          const moved = Math.abs(event.clientX - this.pointerStart.x) + Math.abs(event.clientY - this.pointerStart.y);
          this.pointerStart = null;
          if (moved > 8) return;
          const rect = this.track.getBoundingClientRect();
          const position = (event.clientX - rect.left) / rect.width;
          if (position < 0.4) this.goTo(this.index - 1);
          if (position > 0.6) this.goTo(this.index + 1);
        });
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
      }

      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          const width = this.track.clientWidth || 1;
          const index = Math.round(this.track.scrollLeft / width);
          if (index !== this.index) this.setActive(index, true);
        });
      }

      goTo(target, smooth = true) {
        const count = this.slides.length;
        const index = ((target % count) + count) % count;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // The track is position: relative, so offsetLeft is measured from it.
        this.track.scrollTo({
          left: this.slides[index].offsetLeft,
          behavior: smooth && !reduced ? 'smooth' : 'auto',
        });
        this.setActive(index, false);
      }

      setActive(index, fromScroll) {
        this.index = index;
        const slide = this.slides[index];
        this.slides.forEach((item, i) => item.classList.toggle('is-active', i === index));
        this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        if (this.current) this.current.textContent = String(index + 1);
        if (this.zoom && slide?.dataset.mediaId) this.zoom.dataset.mediaId = slide.dataset.mediaId;
        if (this.status && !fromScroll) this.status.textContent = slide?.getAttribute('aria-label') || '';
        window.pauseAllMedia?.();

        // Keep the active thumbnail visible inside a scrolling thumbnail rail (never scrolls the page).
        const activeThumb = this.thumbs[index];
        const rail = activeThumb?.closest('.ob-gallery__thumbs');
        if (rail && (rail.scrollHeight > rail.clientHeight || rail.scrollWidth > rail.clientWidth)) {
          const item = activeThumb.parentElement;
          rail.scrollTo({
            top: item.offsetTop - rail.clientHeight / 2 + item.offsetHeight / 2,
            left: item.offsetLeft - rail.clientWidth / 2 + item.offsetWidth / 2,
          });
        }
      }

      // Used by Dawn scripts: select the slide whose data-media-id matches.
      setActiveMedia(mediaId) {
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index >= 0) this.goTo(index, false);
      }
    }
  );
}
